import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../8d-logo.png';

const KNOWN_USERS_KEY = 'wt-known-users'; // usernames that have logged in on THIS device

function loadKnownUsers() {
  try { return JSON.parse(localStorage.getItem(KNOWN_USERS_KEY)) || []; }
  catch { return []; }
}
function rememberUser(username) {
  const list = loadKnownUsers().filter(u => u.toLowerCase() !== username.toLowerCase());
  list.unshift(username); // most recent first
  localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(list.slice(0, 30)));
}
function forgetUser(username) {
  const list = loadKnownUsers().filter(u => u.toLowerCase() !== username.toLowerCase());
  localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(list));
  return list;
}

// Eye icons for show/hide password
const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
    ) : (
      <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
    )}
  </svg>
);

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [knownUsers, setKnownUsers] = useState(loadKnownUsers());
  const [search, setSearch] = useState('');
  const pwRef = useRef(null);

  // Show search box only when the list gets long
  const SEARCH_THRESHOLD = 6;
  const filtered = search
    ? knownUsers.filter(u => u.toLowerCase().includes(search.toLowerCase()))
    : knownUsers;

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(form.username.trim(), form.password);
      rememberUser(form.username.trim());
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Check your credentials.');
    } finally { setLoading(false); }
  };

  function pickUser(username) {
    setForm(f => ({ ...f, username }));
    setError('');
    // Jump straight to password — name is already chosen
    setTimeout(() => pwRef.current?.focus(), 50);
  }

  function handleForget(e, username) {
    e.stopPropagation();
    setKnownUsers(forgetUser(username));
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent)', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(7,62,129,0.25)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <img src={logo} alt="8D Studios" style={{ height: 52, width: 'auto', objectFit: 'contain' }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', letterSpacing: '.04em', textTransform: 'uppercase' }}>8D Business OS™</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24, textAlign: 'center' }}>Sign in to your account</div>

        {/* Quick-pick names remembered on this device */}
        {knownUsers.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
              Quick sign in
            </div>
            {knownUsers.length >= SEARCH_THRESHOLD && (
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search your name…"
                style={{ marginBottom: 8, fontSize: 13 }}
              />
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 132, overflowY: 'auto' }}>
              {filtered.map(u => {
                const active = form.username.toLowerCase() === u.toLowerCase();
                return (
                  <button
                    key={u}
                    type="button"
                    onClick={() => pickUser(u)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 10px 6px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background: active ? 'var(--accent-light)' : 'var(--surface)',
                      color: active ? 'var(--accent)' : 'var(--text)',
                      fontWeight: active ? 600 : 400, transition: 'all .12s',
                    }}>
                    {u}
                    <span
                      onClick={e => handleForget(e, u)}
                      title="Remove from this device"
                      style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1, padding: '0 2px' }}>
                      ✕
                    </span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text3)', padding: '4px 0' }}>No match — type your username below</div>
              )}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 0' }} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Username</label>
            <input
              autoFocus={knownUsers.length === 0}
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              placeholder="Enter your username"
            />
          </div>
          <div className="field">
            <label>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                ref={pwRef}
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Enter your password"
                style={{ paddingRight: 42 }}
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                title={showPw ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 6,
                  color: 'var(--text3)', display: 'flex', alignItems: 'center',
                }}>
                <EyeIcon open={showPw} />
              </button>
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>{error}</div>}
          <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 10 }} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
